/** Endpoints DPE — récupération, sauvegarde du payload et calcul. */

import { ApiError, apiFetch } from "@/api/client";

export type DPEState = "brouillon" | "calcule" | "archive";

export type DPEResult = {
  energy_label: string;
  energy_label_detail: { class: string; kwh_ep_per_m2_per_year: number };
  ges_label: string;
  ges_label_detail: { class: string; kgco2_per_m2_per_year: number };
  consumption: {
    primary_total_kwh_per_year: number;
    primary_per_m2_per_year: number;
    final_total_kwh_per_year: number;
    final_by_use_kwh_per_year: Record<string, number>;
  };
  losses_breakdown_w_per_k: Record<string, number>;
  climate: { zone: string; dju: number };
  warnings: { code: string; message: string }[];
  // D17 — répartition immeuble → appartements (§ 17.2.2) si meta.appartements.
  repartition_appartements?: {
    nom: string;
    shab_m2: number;
    Cch: number;
    Becs?: number;
    Cecs?: number;
    Caux_ch?: number;
    Caux_ecs?: number;
    Cecl?: number;
    Cfr?: number;
  }[];
  meta: {
    engine_version: string;
    constants_version: number;
    schema_version: number;
    calculated_at: string;
  };
};

// Type miroir (partiel) du DPEPayload Pydantic — uniquement les segments
// systèmes dont le typage est partagé/exploité côté front. Le payload complet
// reste un `Record<string, unknown>` (saisi par le wizard) ; ces types servent
// de contrat pour les champs 3CL sensibles à l'étiquette (gap analysis C9).
export type HeatingPayload = {
  generator: string;
  share?: number;
  // 3CL § 8 — chaîne émission / distribution / régulation (saisissables ;
  // override du forfait conservateur appliqué par défaut côté moteur).
  emitter?: string;
  distribution?: string;
  distribution_isolated?: boolean;
  regulation?: string;
  // 3CL § 7 — configuration multi-générateurs (base + appoint, etc.).
  config?:
    | "simple"
    | "avec_solaire"
    | "avec_appoint_bois"
    | "poele_avec_sdb_electrique"
    | "systeme_plus_poele_plus_sdb"
    | "chaudiere_releve_bois"
    | "solaire_plus_appoint_bois"
    | "chaudiere_releve_pac"
    | "pac_chaudiere_avec_appoint_bois"
    | "collectif_base_appoint"
    | "multi_installations";
  nominal_power_kw?: number | null;
  // V1.4 — § 9.3/§10 : année d'installation (prime sur l'année de construction).
  year_install?: number | null;
  is_collective?: boolean;
  has_individual_metering?: boolean;
};

export type EcsPayload = {
  generator: string;
  storage_volume_L?: number | null;
  // 3CL § 13 — défaut moteur conservateur = réseau NON isolé (false).
  distribution_isolated?: boolean;
  location?: "volume_habitable" | "hors_volume_habitable";
  // 3CL § 13.2 — ECS collective d'immeuble (réseau de bouclage).
  is_collective_ecs?: boolean;
  collective_network_insulated?: boolean;
  // V1.4 — § 9.3 : année d'installation du générateur ECS.
  year_install?: number | null;
};

export type RenewablePayload = {
  pv_capteurs_m2?: number;
  has_chauffage_solaire?: boolean;
  has_ecs_solaire?: boolean;
  // 3CL § 20.1 — défaut moteur conservateur = installation ancienne (> 5 ans).
  installation_solaire_ancienne?: boolean;
};

export type DPE = {
  id: string;
  logement_id: string;
  version: number;
  state: DPEState;
  is_post_travaux: boolean;
  payload: Record<string, unknown>;
  result: DPEResult | null;
  internal_reference: string;
  calculated_at: string | null;
  engine_version: string | null;
  created_at: string;
  updated_at: string;
};

export function getDpe(logementId: string) {
  return apiFetch<DPE>(`/logements/${logementId}/dpe`);
}

export function updateDpePayload(
  logementId: string,
  payload: Record<string, unknown>,
  opts: { keepalive?: boolean } = {},
) {
  return apiFetch<DPE>(`/logements/${logementId}/dpe`, {
    method: "PATCH",
    body: { payload },
    keepalive: opts.keepalive,
  });
}

export function calculateDpe(logementId: string) {
  return apiFetch<DPE>(`/logements/${logementId}/dpe/calculate`, { method: "POST" });
}

const API_BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api/v1";

export type CalculateProgress = { pct: number; label: string };

/**
 * Variante streaming du calcul DPE (Server-Sent Events).
 *
 * `EventSource` envoie automatiquement les cookies (`withCredentials`),
 * garantissant l'auth session HttpOnly côté backend. Résout le DPE calculé
 * à la réception de l'évènement `result`, rejette une `ApiError` à la
 * réception de l'évènement métier `error` (code/message/details préservés)
 * ou en cas d'échec de la connexion.
 */
export function calculateDpeStreamed(
  logementId: string,
  handlers: {
    onProgress: (p: CalculateProgress) => void;
    signal?: AbortSignal;
  },
): Promise<DPE> {
  const url = `${API_BASE_URL}/logements/${logementId}/dpe/calculate/stream`;
  return new Promise<DPE>((resolve, reject) => {
    const es = new EventSource(url, { withCredentials: true });
    let settled = false;

    function cleanup() {
      es.close();
      if (handlers.signal) handlers.signal.removeEventListener("abort", onAbort);
    }

    function onAbort() {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new ApiError(0, "ABORTED", "Calcul interrompu."));
    }

    if (handlers.signal) {
      if (handlers.signal.aborted) {
        onAbort();
        return;
      }
      handlers.signal.addEventListener("abort", onAbort, { once: true });
    }

    es.addEventListener("progress", (e) => {
      try {
        handlers.onProgress(JSON.parse((e as MessageEvent).data as string) as CalculateProgress);
      } catch {
        /* évènement mal formé : on ignore (la progression reste déterministe) */
      }
    });

    es.addEventListener("result", (e) => {
      if (settled) return;
      settled = true;
      cleanup();
      try {
        resolve(JSON.parse((e as MessageEvent).data as string) as DPE);
      } catch (err) {
        reject(new ApiError(0, "PARSE_ERROR", "Résultat illisible.", err));
      }
    });

    // L'évènement natif `error` couvre deux cas : (1) notre évènement métier
    // `event: error` (porteur de `data`) et (2) une rupture de transport
    // (sans `data`, readyState CLOSED). On distingue via la présence de `data`.
    es.addEventListener("error", (e) => {
      if (settled) return;
      settled = true;
      cleanup();
      const data = (e as MessageEvent).data;
      if (typeof data === "string" && data.length > 0) {
        try {
          const payload = JSON.parse(data) as {
            code: string;
            message: string;
            details?: unknown;
          };
          reject(
            new ApiError(422, payload.code, payload.message, payload.details),
          );
        } catch {
          reject(new ApiError(0, "PARSE_ERROR", "Erreur illisible."));
        }
      } else {
        reject(
          new ApiError(0, "NETWORK_ERROR", "Connexion interrompue pendant le calcul."),
        );
      }
    });
  });
}

/** Récupère les données du rapport (contexte JSON enrichi : étiquettes,
 *  coûts, déperditions %, scénarios, etc.). Sert au rendu React natif
 *  du rapport sur la page DPE. */
export function getReportData(logementId: string) {
  return apiFetch<ReportData>(`/logements/${logementId}/report-data`);
}

// Type miroir du contexte construit par backend/app/domain/reports/context.py
// — uniquement les champs effectivement utilisés par le frontend.
export type ReportData = {
  // Identification
  logement_title: string;
  logement_type_label: string;
  address_line: string;
  postal_code: string;
  city: string;
  construction_year: number | string;
  shab_m2: number | null;
  is_neuf: boolean;
  internal_reference: string;
  estimation_date: string;

  // Étiquettes
  energy_label: string;
  energy_label_color: string;
  energy_kwh_per_m2: number | string;
  ges_label: string;
  ges_label_color: string;
  ges_kgco2_per_m2: number | string;

  // Consommation
  consumption_primary_total: string;
  consumption_primary_per_m2: number | string;
  consumption_final_total: string;
  consumption_final_per_m2: number | string;
  use_breakdown: { label: string; kwh_ef: string }[];

  // Coûts annuels
  annual_costs_rows: {
    use_label: string;
    energy_label: string;
    kwh_ef: string;
    cost_min: string;
    cost_max: string;
  }[];
  annual_costs_total_min: string;
  annual_costs_total_max: string;

  // CO₂
  co2_total_kg_per_year: string;
  co2_equiv_km: string;

  // Déperditions + isolation
  losses_rows: { label: string; value: number | string }[];
  losses_total: number | string;
  losses_pct: { key: string; label: string; value_w_per_k: number; pct: number }[];
  insulation_perf: Record<string, string>;

  // Équipements / enveloppe
  equipment: {
    heating_label: string;
    ecs_label: string;
    ventilation_label: string;
  };
  envelope: {
    walls: string[];
    roof: string | null;
    low_floor: string | null;
    windows: string[];
    doors: string[];
  };

  // Climat
  climate_zone: string;
  climate_dju: number | string;

  // Scénarios et recommandations
  scenarios_detail: {
    name: string;
    improvements: string[];
    label_before: string;
    label_before_color: string;
    primary_per_m2_before: number | string;
    ges_per_m2_before: number | string;
    new_energy_label: string;
    new_energy_color: string;
    new_ges_label: string;
    new_ges_color: string;
    new_consumption_per_m2: number | string;
    new_ges_per_m2: number | string;
    final_kwh_before: number | string;
    final_kwh_after: number | string;
    annual_eur_saved: number | string;
    cost_min: string;
    cost_max: string;
    aids_total: string;
    aids_mpr: string;
    aids_cee: string;
    roi_years: number | string;
    tier: "bleu" | "jaune" | "violet" | "rose" | null;
    relevance: "excellent" | "bon" | "ok" | "marginal" | "non_rentable" | "inadapte";
    relevance_message: string | null;
    ampleur_eligible: boolean;
    accompagnateur_aid_eur: string | null;
  }[];
  actions: { poste: string; deperdition_w_per_k: string; suggestion: string }[];

  // D17 — Répartition immeuble → appartements (§ 17.2.2, vide si non
  // applicable). kWh par appartement et par usage.
  repartition_appartements?: {
    nom: string;
    shab_m2: number;
    Cch: number;
    Becs?: number;
    Cecs?: number;
    Caux_ch?: number;
    Caux_ecs?: number;
    Cecl?: number;
    Cfr?: number;
  }[];

  // V4 — Auto-recommandations chiffrées : pour chaque amélioration du
  // catalog, simulation préalable + filtre ROI < 20 ans, trié par ROI.
  // Affiché à la place du fallback "actions" si non vide.
  auto_recommendations?: {
    key: string;
    label: string;
    description: string;
    label_before: string;
    label_after: string;
    roi_years: number;
    annual_eur_saved: number;
    cost_min_eur: number;
    cost_max_eur: number;
    aids_total_eur: number;
    relevance: "excellent" | "bon";
  }[];

  // V4 — Coûts annuels officiels (guide DPE Annexe 5)
  annual_cost?: {
    central: number;
    min: number;
    max: number;
    reference_year: number;
    by_energy: {
      energy_label: string;
      kwh_per_year: number;
      consumption_eur: number;
      subscription_eur: number;
      total_eur: number;
    }[];
  };

  // V4 — Confort d'été (guide DPE p. 29-30)
  summer_comfort?: {
    classe: "bon" | "moyen" | "insuffisant";
    score: number;
    score_continu?: number;
    roof_well_insulated: boolean;
    protected_facades_pct: number;
    is_traversant: boolean;
    inertia_class: string;
    notes: string[];
  };

  // V4 — Notation enveloppe par poste (guide DPE p. 31)
  insulation_grades?: {
    poste: string;
    poste_label: string;
    u_value: number;
    grade: "tres_bon" | "bon" | "moyen" | "insuffisant";
    grade_label: string;
    grade_color: string;
  }[];

  // V4 — Coûts annuels centraux + référence (rapport ReportView)
  annual_costs_central?: string | null;
  annual_costs_reference_year?: number | null;

  // Bâti ancien (avant 1948) — conseils + précautions. null si non concerné.
  bati_ancien?: {
    is_ancien: boolean;
    detected_by: string[];
    construction_year: number | null;
    perspirant_walls: boolean;
    title: string;
    summary: string;
    advice: { type: "do" | "avoid" | "info"; text: string }[];
  } | null;

  // Profils d'usage — facture estimée selon le comportement (sobre / standard
  // / intensif). « standard » = coût conventionnel. Garde-fou : jamais un
  // chiffre théorique unique.
  usage_scenarios?: Record<
    string,
    {
      multiplier: number;
      heating_kwh: number;
      cost_central: number;
      cost_min: number;
      cost_max: number;
    }
  >;

  // (Les déclarations de `summer_comfort` et `insulation_grades` sont
  // unifiées plus haut. Inutile de les redéclarer ici — c'était un
  // doublon hérité d'une fusion qui faisait crasher tsc.)

  // V4 — Surfaces
  reference_surface_m2?: number | null;
  habitable_surface_m2?: number | null;

  // V4 — Conseils gratuits (guide DPE Annexe 2)
  usage_tips?: {
    icon: string;
    title: string;
    description: string;
    saving_pct?: string;
  }[];
  maintenance_tips?: {
    icon: string;
    title: string;
    description: string;
    frequency: string;
    legal: boolean;
  }[];

  // Avertissements / méta
  warnings: { code?: string; message: string }[];
  engine_version: string;
  schema_version: number | string;

  // Mention légale
  disclaimer_title: string;
  disclaimer_body: string;
};
